import React from 'react';

import type { PublicPluginAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { MediaProvider } from '@atlaskit/editor-common/provider-factory/media-provider';
import type { DocNode } from '@atlaskit/adf-schema/doc';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics/analyticsPluginType';

import type {
	GenerateImageProps,
	SuggestPageEmojiProps,
} from '@atlassian/ai-image-generation/types';
import type { AiStreamingOrchestratorPlugin } from '@atlassian/editor-plugin-ai-streaming-orchestrator/types';
import type { PassThroughCommandProps } from '@atlassian/editor-rovo-bridge/command-handler';
import type { AdditionalContext, ContentLocation } from '@atlassian/editor-rovo-bridge/content';

import { fg } from '@confluence/feature-gating';
import { withFlags, type WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';

import { useEditorRovoBridgeCommandListener } from './hooks/useEditorRovoBridgeCommandListener.tsx';

type SendContextReadyCallback = NonNullable<PassThroughCommandProps['onSendContextReady']>;

export interface UseRovoBridgeCommandListenerParams {
	additionalContext?: AdditionalContext;
	editorAPI: PublicPluginAPI<[AnalyticsPlugin, AiStreamingOrchestratorPlugin]> | undefined | null;
	onTitleChange?: (title: string) => void;
	contentId: string;
	contentLocation: ContentLocation;
	setEditorDisabled?: (disabled: boolean) => void;
	forceDraftSync?: () => Promise<DocNode>;
	generateAIHeaderImage?: (props: GenerateImageProps) => Promise<string | undefined>;
	fakeGenerateAIHeaderImage?: () => Promise<void>;
	/** AIFC page emoji suggestion. Invoked once-per-stream once the title is available. */
	suggestPageEmoji?: (props: SuggestPageEmojiProps) => Promise<string | undefined>;
	mediaProvider?: Promise<MediaProvider>; // Image processor for handling media collection IDs
	setTitlePlaceholderState?: (isLoading: boolean) => void;
	useGenericEditorSkill?: boolean;
	/** Called when a streaming operation completes (e.g. create, deep-research-create) */
	onStreamEnd?: () => void;
	/** Called when Rovo has read and assembled context for an outgoing message. */
	onSendContextReady?: SendContextReadyCallback;
	/** Called after rovoBridgeAPI.ready() and ContentReadyCommand have been sent,
	 * guaranteeing both sides of the bridge are ready to communicate. */
	onBridgeReady?: () => void;
	/** Whether the editor is currently in preview mode. When true, content updates
	 * from Rovo chat will be blocked and a warning flag shown to the user. */
	isPreviewMode?: boolean;
	/** When true, the bridge registers even when `editorAPI.aiExperience` is
	 * absent. Used by the AI Planner bridge route where the AI UI is disabled
	 * but streaming must still work. See CIAP-136. */
	skipAiExperienceRequirement?: boolean;
}

export type ExtraCommandProps = Pick<
	UseRovoBridgeCommandListenerParams,
	'forceDraftSync' | 'generateAIHeaderImage'
> & {
	reuploadImages?: (adfDoc: DocNode) => Promise<void>;
};

const EditorRovoBridgeCommandListenerImpl = (
	props: UseRovoBridgeCommandListenerParams & WithFlagsProps,
) => {
	useEditorRovoBridgeCommandListener(props);
	return null;
};

const RovoBridgeCommandListenerInternal = (
	props: UseRovoBridgeCommandListenerParams & WithFlagsProps,
) => {
	if (!fg('aifc_create_enabled')) {
		return null;
	}

	return <EditorRovoBridgeCommandListenerImpl {...props} />;
};

export const RovoBridgeCommandListener: React.ComponentType<
	Omit<UseRovoBridgeCommandListenerParams, 'flags'>
> = withFlags(RovoBridgeCommandListenerInternal);
